import { zodResolver } from '@hookform/resolvers/zod';
import { TextField } from '@mui/material';
import IncomeOrExpenseTypeSelector from 'components/new/IncomeOrExpenseTypeSelector';
import { PartnersCapitalCategory, Schema } from 'further-types/lp/account';
import {
  ExpenseType,
  IncomeExpenseCategoryType,
} from 'further-types/lp/income-and-expenses';
import BasicSelect from 'further-ui/components/BasicSelect';
import FormField from 'further-ui/components/FormField';
import { Column } from 'further-ui/components/Layout';
import {
  AccountSectionLabels,
  BalanceSheetSectionLabels,
  BalanceSheetSectionsByAccountSection,
  IncomeAndExpensesLabels,
  PartnersCapitalCategoryLabels,
} from 'further-ui/labels';
import { account as rules } from 'further-ui/validations';
import api from 'lib/trpcClient';
import { forwardRef, useImperativeHandle } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

type FormValues = z.infer<typeof rules.CreateAccount>;

export type AccountFormContentRef = { submit: () => void };

type Props = {
  vehicleId: string;
  onSubmit: (data: FormValues) => void;
  prepaidExpenseAccountId?: string;
  deferredIncomeAccountId?: string;
  isCashAccount?: boolean;
  account?: {
    id: string;
    section: Schema.AccountSection;
    accountName: string;
    accountCode: string;
    payableReceivableCategory?: Schema.PayableReceivableCategory | null;
    partnersCapitalCategory?: PartnersCapitalCategory | null;
    incomeExpenseCategory?: string | null;
    customActionTypeId?: string | null;
    customActionTypeName?: string | null;
    balanceSheetSection?: Schema.BalanceSheetSection | null;
    transactionsCount: number;
  };
};

const sections = Object.entries(AccountSectionLabels).map(([key, label]) => ({
  key,
  label,
}));

const AccountFormContent = forwardRef<AccountFormContentRef, Props>(
  (
    {
      vehicleId,
      account,
      onSubmit,
      prepaidExpenseAccountId,
      deferredIncomeAccountId,
      isCashAccount,
    },
    ref,
  ) => {
    const { control, handleSubmit, watch } = useForm<FormValues>({
      resolver: zodResolver(rules.CreateAccount),
      shouldUnregister: true,
      defaultValues: {
        section: account?.section,
        accountName: account?.accountName ?? '',
        accountCode: account?.accountCode ?? '',
        payableReceivableCategory: account?.payableReceivableCategory ?? null,
        balanceSheetSection:
          account?.balanceSheetSection ??
          (isCashAccount
            ? Schema.BalanceSheetSection.CashAndCashEquivalents
            : undefined),
        partnersCapitalCategory: account?.partnersCapitalCategory ?? null,
        incomeExpenseCategory:
          account?.customActionTypeName ??
          account?.incomeExpenseCategory ??
          null,
      },
    });

    useImperativeHandle(ref, () => ({ submit: handleSubmit(onSubmit) }), [
      handleSubmit,
      onSubmit,
    ]);

    const section = watch('section');

    const showBalanceSheetSection =
      section === Schema.AccountSection.Assets ||
      section === Schema.AccountSection.Payables;

    const shouldShowIncomeExpenseCategory =
      section === Schema.AccountSection.Income ||
      section === Schema.AccountSection.Expenses;

    const shouldShowPartnersCapitalCategory =
      section === Schema.AccountSection.PartnersCapital;

    const { data: typeOptions } = api.incomeAndExpenses.getTypeOptions.useQuery(
      { vehicleId },
      { enabled: shouldShowIncomeExpenseCategory },
    );

    const partnersCapitalCategoryOptions = Object.entries(
      PartnersCapitalCategoryLabels,
    ).map(([value, label]) => ({
      value: value as PartnersCapitalCategory,
      label,
    }));

    const balanceSheetSectionOptions =
      showBalanceSheetSection && section
        ? BalanceSheetSectionsByAccountSection[section].map((key) => ({
            key,
            label: BalanceSheetSectionLabels[key],
          }))
        : [];

    const prepaidExpenseAccountExists = !!(
      prepaidExpenseAccountId && prepaidExpenseAccountId !== account?.id
    );
    const deferredIncomeAccountExists = !!(
      deferredIncomeAccountId && deferredIncomeAccountId !== account?.id
    );

    const categoryOptions = [
      ...(section === Schema.AccountSection.Assets
        ? [
            {
              key: Schema.PayableReceivableCategory.ReceivableOperations,
              label: 'Receivables (operations)',
            },
            {
              key: Schema.PayableReceivableCategory.ReceivableInvestment,
              label: 'Receivables (investment)',
            },
            {
              key: Schema.PayableReceivableCategory.ReceivableCapital,
              label: 'Receivables (capital)',
            },
            {
              key: Schema.PayableReceivableCategory.ReceivableBankTransfer,
              label: 'Receivables (bank transfer)',
            },
            ...(prepaidExpenseAccountExists
              ? []
              : [
                  {
                    key: Schema.PayableReceivableCategory.PrepaidExpenses,
                    label: 'Prepaid expenses',
                  },
                ]),
          ]
        : []),
      ...(section === Schema.AccountSection.Payables
        ? [
            {
              key: Schema.PayableReceivableCategory.PayableOperations,
              label: 'Payables (operations)',
            },
            {
              key: Schema.PayableReceivableCategory.PayableInvestment,
              label: 'Payables (investment)',
            },
            {
              key: Schema.PayableReceivableCategory.PayableCapital,
              label: 'Payables (capital)',
            },
            ...(deferredIncomeAccountExists
              ? []
              : [
                  {
                    key: Schema.PayableReceivableCategory.DeferredIncome,
                    label: 'Deferred income',
                  },
                ]),
          ]
        : []),
    ];

    const isIncomeSection = section === Schema.AccountSection.Income;
    const customTypeOptions = isIncomeSection
      ? typeOptions?.incomeTypeOptions ?? []
      : typeOptions?.expenseTypeOptions ?? [];

    const expenseTypeValues = new Set<string>(Object.values(ExpenseType));

    const builtInIncomeExpenseOptions = Object.values(IncomeExpenseCategoryType)
      .filter((value) =>
        isIncomeSection
          ? !expenseTypeValues.has(value)
          : expenseTypeValues.has(value),
      )
      .map((value) => ({
        value,
        label: IncomeAndExpensesLabels[value],
      }));

    const incomeExpenseCategoryOptions = [
      ...builtInIncomeExpenseOptions,
      ...customTypeOptions.map((opt) => ({
        value: opt.label,
        label: opt.label,
        customActionTypeId: opt.customActionTypeId,
      })),
    ];

    const typeLabel = isIncomeSection ? 'income type' : 'expense type';

    return (
      <form>
        <Column spacing="lg">
          <FormField label="General ledger type">
            <Controller
              name="section"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <BasicSelect
                  value={field.value}
                  onChange={field.onChange}
                  options={sections.map((s) => ({
                    value: s.key as Schema.AccountSection,
                    label: s.label,
                  }))}
                  placeholder="Select general ledger type"
                  error={!!error}
                  helperText={error?.message}
                  fullWidth
                  disabled={!!account}
                />
              )}
            />
          </FormField>
          <FormField label="Account code">
            <Controller
              name="accountCode"
              control={control}
              render={({ field: { ref, ...rest }, fieldState: { error } }) => (
                <TextField
                  {...rest}
                  inputRef={ref}
                  size="small"
                  placeholder="Enter account code"
                  error={!!error}
                  helperText={error?.message}
                  fullWidth
                />
              )}
            />
          </FormField>
          <FormField label="Account name">
            <Controller
              name="accountName"
              control={control}
              render={({ field: { ref, ...rest }, fieldState: { error } }) => (
                <TextField
                  {...rest}
                  size="small"
                  inputRef={ref}
                  placeholder="Enter account name"
                  error={!!error}
                  helperText={error?.message}
                  fullWidth
                />
              )}
            />
          </FormField>
          {shouldShowPartnersCapitalCategory && (
            <FormField label="Partners' capital category">
              <Controller
                name="partnersCapitalCategory"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <BasicSelect
                    value={field.value}
                    onChange={field.onChange}
                    options={partnersCapitalCategoryOptions}
                    placeholder="Partners' capital category"
                    error={!!error}
                    helperText={error?.message}
                    fullWidth
                  />
                )}
              />
            </FormField>
          )}
          {showBalanceSheetSection && (
            <FormField label="Balance sheet section">
              <Controller
                name="balanceSheetSection"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <BasicSelect
                    value={field.value}
                    onChange={field.onChange}
                    options={balanceSheetSectionOptions.map((o) => ({
                      value: o.key,
                      label: o.label,
                    }))}
                    placeholder="Balance sheet section"
                    error={!!error}
                    helperText={error?.message}
                    fullWidth
                    disabled={isCashAccount}
                  />
                )}
              />
            </FormField>
          )}
          {showBalanceSheetSection && (
            <FormField label="Payable/receivable category">
              <Controller
                name="payableReceivableCategory"
                control={control}
                render={({
                  field: { value, ...field },
                  fieldState: { error },
                }) => (
                  <BasicSelect
                    value={value ?? ''}
                    onChange={field.onChange}
                    options={categoryOptions.map((o) => ({
                      value: o.key,
                      label: o.label,
                    }))}
                    placeholder="Select payable/receivable category"
                    error={!!error}
                    helperText={error?.message}
                    fullWidth
                  />
                )}
              />
            </FormField>
          )}
          {shouldShowIncomeExpenseCategory && (
            <FormField label="Income/expense category">
              <Controller
                name="incomeExpenseCategory"
                control={control}
                render={({
                  field: { value, onChange, ...field },
                  fieldState: { error },
                }) => (
                  <IncomeOrExpenseTypeSelector
                    {...field}
                    value={typeof value === 'string' ? value : undefined}
                    options={incomeExpenseCategoryOptions}
                    typeLabel={typeLabel}
                    placeholder="Select or type income/expense category"
                    error={error}
                    onChange={onChange}
                  />
                )}
              />
            </FormField>
          )}
        </Column>
      </form>
    );
  },
);

AccountFormContent.displayName = 'AccountFormContent';

export default AccountFormContent;
